/**
 * Documents domain API functions (Step 7.1.7).
 *
 * Document CRUD for all entity types: unit, tenant, landlord, property, owner.
 * Each entity type has: fetch, upload, downloadUrl, delete.
 * Extracted from adminData/index.js for team scalability.
 */
import {
  API_BASE_URL,
  getApiHeaders,
  getApiHeadersMultipart,
  parseAdminErrorResponse,
} from "./core";

// ── Unit Documents ──────────────────────────────────────────────────────

export function fetchAdminUnitDocuments(unitId) {
  return fetch(
    `${API_BASE_URL}/api/admin/unit-documents?unit_id=${encodeURIComponent(unitId)}`,
    { headers: getApiHeaders() }
  )
    .then((res) => {
      if (!res.ok) throw new Error("Dokumente konnten nicht geladen werden.");
      return res.json();
    })
    .then((data) => {
      if (data != null && typeof data === "object" && Array.isArray(data.items)) {
        return data.items;
      }
      throw new Error('Ungültige Antwort: erwartet { items: [] }.');
    });
}

export async function uploadAdminUnitDocument(unitId, file, options = {}) {
  const fd = new FormData();
  fd.append("unit_id", unitId);
  fd.append("file", file);
  const cat = options.category != null ? String(options.category).trim() : "";
  if (cat) fd.append("category", cat);
  const res = await fetch(`${API_BASE_URL}/api/admin/unit-documents`, {
    method: "POST",
    headers: getApiHeadersMultipart(),
    body: fd,
  });
  if (!res.ok) {
    throw new Error(await parseAdminErrorResponse(res));
  }
  return res.json();
}

export function fetchAdminUnitDocumentDownloadUrl(documentId) {
  return fetch(
    `${API_BASE_URL}/api/admin/unit-documents/${encodeURIComponent(documentId)}/download`,
    { headers: getApiHeaders() }
  ).then(async (res) => {
    if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
    return res.json();
  });
}

export async function deleteAdminUnitDocument(documentId) {
  const res = await fetch(`${API_BASE_URL}/api/admin/unit-documents/${encodeURIComponent(documentId)}`, {
    method: "DELETE",
    headers: getApiHeaders(),
  });
  if (!res.ok) {
    throw new Error(await parseAdminErrorResponse(res));
  }
  return res.json();
}

// ── Tenant Documents ────────────────────────────────────────────────────

export function fetchAdminTenantDocuments(tenantId) {
  return fetch(
    `${API_BASE_URL}/api/admin/tenant-documents?tenant_id=${encodeURIComponent(tenantId)}`,
    { headers: getApiHeaders() }
  )
    .then((res) => {
      if (!res.ok) throw new Error("Dokumente konnten nicht geladen werden.");
      return res.json();
    })
    .then((data) => {
      if (data != null && typeof data === "object" && Array.isArray(data.items)) {
        return data.items;
      }
      throw new Error('Ungültige Antwort: erwartet { items: [] }.');
    });
}

export async function uploadAdminTenantDocument(tenantId, file, options = {}) {
  const fd = new FormData();
  fd.append("tenant_id", tenantId);
  fd.append("file", file);
  const cat = options.category != null ? String(options.category).trim() : "";
  if (cat) fd.append("category", cat);
  const res = await fetch(`${API_BASE_URL}/api/admin/tenant-documents`, {
    method: "POST",
    headers: getApiHeadersMultipart(),
    body: fd,
  });
  if (!res.ok) {
    throw new Error(await parseAdminErrorResponse(res));
  }
  return res.json();
}

export function fetchAdminTenantDocumentDownloadUrl(documentId) {
  return fetch(
    `${API_BASE_URL}/api/admin/tenant-documents/${encodeURIComponent(documentId)}/download`,
    { headers: getApiHeaders() }
  ).then(async (res) => {
    if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
    return res.json();
  });
}

export async function deleteAdminTenantDocument(documentId) {
  const res = await fetch(`${API_BASE_URL}/api/admin/tenant-documents/${encodeURIComponent(documentId)}`, {
    method: "DELETE",
    headers: getApiHeaders(),
  });
  if (!res.ok) {
    throw new Error(await parseAdminErrorResponse(res));
  }
  return res.json();
}

// ── Tenancy Documents ───────────────────────────────────────────────────

export function fetchAdminTenancyDocuments(tenancyId) {
  return fetch(
    `${API_BASE_URL}/api/admin/tenancy-documents?tenancy_id=${encodeURIComponent(tenancyId)}`,
    { headers: getApiHeaders() }
  )
    .then((res) => {
      if (!res.ok) throw new Error("Dokumente konnten nicht geladen werden.");
      return res.json();
    })
    .then((data) => {
      if (data != null && typeof data === "object" && Array.isArray(data.items)) {
        return data.items;
      }
      throw new Error('Ungültige Antwort: erwartet { items: [] }.');
    });
}

export async function uploadAdminTenancyDocument(tenancyId, file, options = {}) {
  const fd = new FormData();
  fd.append("tenancy_id", tenancyId);
  fd.append("file", file);
  const cat = options.category != null ? String(options.category).trim() : "";
  fd.append("category", cat);
  const res = await fetch(`${API_BASE_URL}/api/admin/tenancy-documents`, {
    method: "POST",
    headers: getApiHeadersMultipart(),
    body: fd,
  });
  if (!res.ok) {
    throw new Error(await parseAdminErrorResponse(res));
  }
  return res.json();
}

export function fetchAdminTenancyDocumentDownloadUrl(documentId) {
  return fetch(
    `${API_BASE_URL}/api/admin/tenancy-documents/${encodeURIComponent(documentId)}/download`,
    { headers: getApiHeaders() }
  ).then(async (res) => {
    if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
    return res.json();
  });
}

export async function deleteAdminTenancyDocument(documentId) {
  const res = await fetch(`${API_BASE_URL}/api/admin/tenancy-documents/${encodeURIComponent(documentId)}`, {
    method: "DELETE",
    headers: getApiHeaders(),
  });
  if (!res.ok) {
    throw new Error(await parseAdminErrorResponse(res));
  }
  return res.json();
}

// ── Landlord Documents ──────────────────────────────────────────────────

export function fetchAdminLandlordDocuments(landlordId) {
  return fetch(
    `${API_BASE_URL}/api/admin/landlord-documents?landlord_id=${encodeURIComponent(landlordId)}`,
    { headers: getApiHeaders() }
  )
    .then((res) => {
      if (!res.ok) throw new Error("Dokumente konnten nicht geladen werden.");
      return res.json();
    })
    .then((data) => {
      if (data != null && typeof data === "object" && Array.isArray(data.items)) {
        return data.items;
      }
      throw new Error('Ungültige Antwort: erwartet { items: [] }.');
    });
}

export async function uploadAdminLandlordDocument(landlordId, file, options = {}) {
  const fd = new FormData();
  fd.append("landlord_id", landlordId);
  fd.append("file", file);
  const cat = options.category != null ? String(options.category).trim() : "";
  if (cat) fd.append("category", cat);
  const res = await fetch(`${API_BASE_URL}/api/admin/landlord-documents`, {
    method: "POST",
    headers: getApiHeadersMultipart(),
    body: fd,
  });
  if (!res.ok) {
    throw new Error(await parseAdminErrorResponse(res));
  }
  return res.json();
}

export function fetchAdminLandlordDocumentDownloadUrl(documentId) {
  return fetch(
    `${API_BASE_URL}/api/admin/landlord-documents/${encodeURIComponent(documentId)}/download`,
    { headers: getApiHeaders() }
  ).then(async (res) => {
    if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
    return res.json();
  });
}

export async function deleteAdminLandlordDocument(documentId) {
  const res = await fetch(`${API_BASE_URL}/api/admin/landlord-documents/${encodeURIComponent(documentId)}`, {
    method: "DELETE",
    headers: getApiHeaders(),
  });
  if (!res.ok) {
    throw new Error(await parseAdminErrorResponse(res));
  }
  return res.json();
}

// ── Property Documents ──────────────────────────────────────────────────

export function fetchAdminPropertyDocuments(propertyId) {
  return fetch(
    `${API_BASE_URL}/api/admin/property-documents?property_id=${encodeURIComponent(propertyId)}`,
    { headers: getApiHeaders() }
  )
    .then((res) => {
      if (!res.ok) throw new Error("Dokumente konnten nicht geladen werden.");
      return res.json();
    })
    .then((data) => {
      if (data != null && typeof data === "object" && Array.isArray(data.items)) {
        return data.items;
      }
      throw new Error('Ungültige Antwort: erwartet { items: [] }.');
    });
}

export async function uploadAdminPropertyDocument(propertyId, file, options = {}) {
  const fd = new FormData();
  fd.append("property_id", propertyId);
  fd.append("file", file);
  const cat = options.category != null ? String(options.category).trim() : "";
  if (cat) fd.append("category", cat);
  const res = await fetch(`${API_BASE_URL}/api/admin/property-documents`, {
    method: "POST",
    headers: getApiHeadersMultipart(),
    body: fd,
  });
  if (!res.ok) {
    throw new Error(await parseAdminErrorResponse(res));
  }
  return res.json();
}

export function fetchAdminPropertyDocumentDownloadUrl(documentId) {
  return fetch(
    `${API_BASE_URL}/api/admin/property-documents/${encodeURIComponent(documentId)}/download`,
    { headers: getApiHeaders() }
  ).then(async (res) => {
    if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
    return res.json();
  });
}

export async function deleteAdminPropertyDocument(documentId) {
  const res = await fetch(`${API_BASE_URL}/api/admin/property-documents/${encodeURIComponent(documentId)}`, {
    method: "DELETE",
    headers: getApiHeaders(),
  });
  if (!res.ok) {
    throw new Error(await parseAdminErrorResponse(res));
  }
  return res.json();
}

// ── Owner Documents ─────────────────────────────────────────────────────

export function fetchAdminOwnerDocuments(ownerId) {
  return fetch(
    `${API_BASE_URL}/api/admin/owner-documents?owner_id=${encodeURIComponent(ownerId)}`,
    { headers: getApiHeaders() }
  )
    .then((res) => {
      if (!res.ok) throw new Error("Dokumente konnten nicht geladen werden.");
      return res.json();
    })
    .then((data) => {
      if (data != null && typeof data === "object" && Array.isArray(data.items)) {
        return data.items;
      }
      throw new Error('Ungültige Antwort: erwartet { items: [] }.');
    });
}

export async function uploadAdminOwnerDocument(ownerId, file, options = {}) {
  const fd = new FormData();
  fd.append("owner_id", ownerId);
  fd.append("file", file);
  const cat = options.category != null ? String(options.category).trim() : "";
  if (cat) fd.append("category", cat);
  const res = await fetch(`${API_BASE_URL}/api/admin/owner-documents`, {
    method: "POST",
    headers: getApiHeadersMultipart(),
    body: fd,
  });
  if (!res.ok) {
    throw new Error(await parseAdminErrorResponse(res));
  }
  return res.json();
}

export function fetchAdminOwnerDocumentDownloadUrl(documentId) {
  return fetch(
    `${API_BASE_URL}/api/admin/owner-documents/${encodeURIComponent(documentId)}/download`,
    { headers: getApiHeaders() }
  ).then(async (res) => {
    if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
    return res.json();
  });
}

export async function deleteAdminOwnerDocument(documentId) {
  const res = await fetch(`${API_BASE_URL}/api/admin/owner-documents/${encodeURIComponent(documentId)}`, {
    method: "DELETE",
    headers: getApiHeaders(),
  });
  if (!res.ok) {
    throw new Error(await parseAdminErrorResponse(res));
  }
  return res.json();
}
